import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import MaskedTextField from '@/common/components/MaskedTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import Select from '@/common/components/Select'
import TextField from '@/common/components/TextField'
import { percentageMaskAllowNegative } from '@/common/constants/masks.constant'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { formatCurrency, formatPercentage } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useUpdateContractFeeDetails } from '@/features/establishments/hooks/contract/useUpdateContractFeeDetails'
import {
  UpdateContractFeeDetailsSchema,
  updateContractFeeDetailsSchema,
} from '@/features/establishments/schemas/contract/updateFeeDetails.schema'
import { EstablishmentContractType } from '@/features/establishments/types/establishmentContract.type'
import { useFeeTemplates } from '@/features/feeTemplates/hooks/useFeeTemplates'
import { FeeTemplateData } from '@/features/feeTemplates/types/feeTemplates.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'

interface UpdateFeeDetailsModalProps {
  contract?: EstablishmentContractType
}

export const UpdateFeeDetailsModal = ({ contract }: UpdateFeeDetailsModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchFeeTemplate, setSearchFeeTemplate] = useDebounce('')

  const { data: feeTemplates } = useFeeTemplates<FeeTemplateData[]>({
    filters: { search: searchFeeTemplate, category: 'establishment' },
  })

  const formMethods = useForm<UpdateContractFeeDetailsSchema>({
    resolver: zodResolver(updateContractFeeDetailsSchema),
  })

  const {
    control,
    register,
    watch,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = formMethods

  const {
    fields: contractFeesFields,
    append: appendContractFee,
    remove: removeContractFee,
  } = useFieldArray({
    control,
    name: 'contract.contract_fees_attributes',
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['establishmentContract', contract?.id] })
    setIsOpen(false)
  }

  const { mutate: updateContractFeeDetails, isPending: updateContractFeeDetailsIsPending } =
    useUpdateContractFeeDetails({
      contractId: contract?.id,
      currentContractFees: contract?.contract_fees,
      onSuccess,
      onError: FormTransformers.errorsTransformer<UpdateContractFeeDetailsSchema>({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<UpdateContractFeeDetailsSchema> = (data) => {
    updateContractFeeDetails(data)
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        contract: {
          contract_fees_attributes:
            contract?.contract_fees?.map((fee) => ({
              fee_id: fee.id,
              fee_template_id: fee.fee_template.id,
              display_name: fee.fee_template.display_name,
              value:
                fee.fee_template.calculate_as === 'percentage'
                  ? formatPercentage(Number(fee.value))
                  : formatCurrency(Number(fee.value)),
              calculate_as: fee.fee_template.calculate_as,
              main_fee: fee.main_fee,
            })) || [],
        },
      })
    } else {
      reset()
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_fee_details_modal"
          operationKlassToCheckPermission={'Admin::Client::Contracts::UpdateContractBillInformation'}
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando Tarifas"
      dataTestid="update_fee_details"
      bodyClassName="max-h-[calc(100vh-10rem)] overflow-y-auto"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateContractFeeDetailsIsPending}
      confirmButtonIsLoading={updateContractFeeDetailsIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <Select
              placeholder="Pesquisar Tarifa"
              value={null}
              onInputChange={setSearchFeeTemplate}
              onChange={(value) => {
                const feeTemplate = feeTemplates.find((template) => template.id === (value as Option).value)

                if (!feeTemplate) return

                appendContractFee({
                  fee_template_id: feeTemplate?.id,
                  display_name: feeTemplate?.display_name,
                  value: feeTemplate.default_value
                    ? feeTemplate?.calculate_as === 'percentage'
                      ? formatPercentage(Number(feeTemplate.default_value))
                      : formatCurrency(Number(feeTemplate.default_value))
                    : '',
                  calculate_as: feeTemplate?.calculate_as,
                  main_fee: false,
                })
              }}
              options={
                Mapper.mapToOptions<FeeTemplateData>({ labelFieldName: 'display_name', valueFieldName: 'id' })(
                  feeTemplates,
                )?.filter((feeTemplate) => {
                  return !contractFeesFields?.some(({ fee_template_id }) => fee_template_id === feeTemplate.value)
                }) || []
              }
              isDisabled={updateContractFeeDetailsIsPending}
            />

            {contractFeesFields?.map((contractFee, index) => (
              <div key={contractFee.fee_template_id} className="flex items-start gap-x-2">
                <Button
                  variant="outline-text"
                  className="text-danger hover:text-danger-soft transition-colors p-0 mt-[2.0625rem]"
                  onClick={() => removeContractFee(index)}
                  disabled={updateContractFeeDetailsIsPending}
                >
                  <XIcon strokeWidth={1} className="size-6" />
                </Button>

                <div className="w-2/3 flex flex-col gap-y-1">
                  <TextField label="Categoria" defaultValue={contractFee.display_name} disabled />

                  {contractFee.calculate_as === 'percentage' && (
                    <Checkbox
                      label="Tarifa principal"
                      className="gap-2"
                      labelClassName="text-[10px] font-light italic"
                      dataTestidPrefix="contract_fees_main_fee"
                      defaultChecked={
                        watch(`contract.contract_fees_attributes.${index}.main_fee`) || contractFee.main_fee
                      }
                      {...register(`contract.contract_fees_attributes.${index}.main_fee`)}
                      errorMessage={errors?.contract?.contract_fees_attributes?.[index]?.main_fee?.message}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        register(`contract.contract_fees_attributes.${index}.main_fee`).onChange(event)
                      }}
                      disabled={updateContractFeeDetailsIsPending}
                    />
                  )}
                </div>

                {contractFee.calculate_as === 'percentage' ? (
                  <MaskedTextField
                    mask={percentageMaskAllowNegative}
                    label="Valor"
                    className="w-1/3"
                    {...register(`contract.contract_fees_attributes.${index}.value`)}
                    errorMessage={errors?.contract?.contract_fees_attributes?.[index]?.value?.message}
                    disabled={updateContractFeeDetailsIsPending || !!contractFee.fee_id}
                  />
                ) : (
                  <Controller
                    name={`contract.contract_fees_attributes.${index}.value`}
                    control={control}
                    disabled={updateContractFeeDetailsIsPending || !!contractFee.fee_id}
                    render={({ field }) => (
                      <CurrencyTextField
                        label="Valor"
                        className="w-1/3"
                        {...field}
                        errorMessage={errors?.contract?.contract_fees_attributes?.[index]?.value?.message}
                      />
                    )}
                  />
                )}
              </div>
            ))}
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
